import { useCallback, useEffect, useState } from 'react'
import { api } from '../api'
import { Icon } from './Icon'
import { Confirm } from './Confirm'
import {
  EMPTY_GLOSSARY,
  EMPTY_STYLE,
  LONGEST_RULE,
  MOST_RULES,
  parseGlossary,
  parseStyleGuide,
  serializeGlossary,
  serializeStyleGuide,
  type Glossary,
  type StyleGuide,
} from '@staleless/schema'

const GLOSSARY = 'glossary.yaml'
const STYLE = 'style.yaml'

/**
 * The words this documentation uses, and how it is written — as forms.
 *
 * Both were real content with real readers: the glossary reaches the published
 * search index as aliases and synonym groups, and both travel with every
 * request for help with the words. Both were also edited by opening a YAML
 * file in the Markdown editor. A writer maintaining their product's vocabulary
 * was writing `not: [organisation, org]` by hand, learning the format from the
 * comments in the starter file, and finding out about a mistake from the lint.
 *
 * A row per term and a line per rule instead. The file is still the format —
 * the whole-site menu still opens it, and this writes the same YAML through
 * the same staged path a page uses, so the change is reviewed and published
 * like any other. What goes is having to know the shape before you can add a
 * word.
 */
export function WordsScreen({
  branch,
  readOnly,
  onStaged,
  onOpenPage,
  ensureDraft,
}: {
  branch: string | null
  readOnly: boolean
  onStaged: () => void
  /** The file itself, for somebody who would rather write the YAML. */
  onOpenPage: (path: string) => void
  /** The draft this edit needs, made on demand — see the Variables screen. */
  ensureDraft?: () => Promise<string | null>
}) {
  const [glossary, setGlossary] = useState<Glossary | null>(null)
  const [style, setStyle] = useState<StyleGuide | null>(null)
  /** The bytes each file had when it was read, so a write keeps its comments. */
  const [raw, setRaw] = useState<{ glossary: string; style: string }>({ glossary: '', style: '' })
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dropping, setDropping] = useState<string | null>(null)

  const [newUse, setNewUse] = useState('')
  const [newNot, setNewNot] = useState('')
  const [newBecause, setNewBecause] = useState('')
  const [newVoice, setNewVoice] = useState('')
  const [newAvoid, setNewAvoid] = useState('')

  const load = useCallback(
    async (on: string | null = branch) => {
      /*
       * A missing file is an EMPTY one, not a failure. Most projects have
       * neither of these until somebody writes the first term, and a screen
       * that reports "not found" for the ordinary case is a screen that reads
       * as broken.
       */
      const read = async (path: string) =>
        api
          .page(path, on ?? undefined)
          .then((page) => page.content)
          .catch(() => '')
      try {
        const [g, s] = await Promise.all([read(GLOSSARY), read(STYLE)])
        setRaw({ glossary: g, style: s })
        setGlossary(g.trim() === '' ? EMPTY_GLOSSARY : parseGlossary(g))
        setStyle(s.trim() === '' ? EMPTY_STYLE : parseStyleGuide(s))
        setError(null)
      } catch (e) {
        // A file somebody has broken by hand. Say so, and say where.
        setGlossary(EMPTY_GLOSSARY)
        setStyle(EMPTY_STYLE)
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [branch],
  )
  useEffect(() => void load(), [load])

  /** Write one of the two files back, on the draft this edit makes if it must. */
  const save = async (path: string, content: string, done: string) => {
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return
    setBusy(true)
    setNote(null)
    try {
      await api.write(on, path, content)
      await load(on)
      onStaged()
      setError(null)
      setNote(done)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const saveGlossary = (next: Glossary, done: string) =>
    save(GLOSSARY, serializeGlossary(next, raw.glossary || undefined), done)
  const saveStyle = (next: StyleGuide, done: string) =>
    save(STYLE, serializeStyleGuide(next, raw.style || undefined), done)

  if (!glossary || !style) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Words &amp; style</h1>
        </header>
        <div className="screen__loading" aria-busy="true">
          <p className="sr-only">Loading…</p>
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      </div>
    )
  }

  const rules = style.voice.length + style.avoid.length

  return (
    <div className="screen" data-words-screen>
      <header className="screen__head">
        <h1>Words &amp; style</h1>
        <p className="screen__sub">
          The word this documentation uses for a thing, and the ones it does not — and the short
          rules that say how it is written. Both reach the search a reader uses and every suggestion
          the writing help makes.
        </p>
      </header>

      {readOnly && (
        <p className="screen__warn">
          <Icon name="info" size={13} /> You have view access to this site, so these are read-only.
        </p>
      )}
      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {note && !error && (
        <p className="screen__note" role="status">
          <Icon name="check" size={13} /> {note}
        </p>
      )}

      <section className="home__card home__card--wide">
        <h2>Words</h2>
        <p className="home__quiet">
          A reader who searches for the word your interface showed them and lands on a page using a
          different one does not think “synonym” — they think the page is about something else. The
          words you replace become search aliases, so both spellings find the page either way. It is
          a warning to a writer, never a refusal.
        </p>

        {glossary.terms.length === 0 ? (
          <p className="nothing">
            No words yet. The first one is usually the three names every product has for the place
            work lives.
          </p>
        ) : (
          <ul className="vars__list">
            {glossary.terms.map((term) => (
              <li className="vars__row" key={term.use} data-word={term.use}>
                <div className="vars__line">
                  <code className="vars__name">{term.use}</code>
                  <span className="vars__meta">
                    {/* Not an empty state: a word with nothing to replace is a
                        word this documentation simply prefers, which is a
                        legitimate entry and the commonest first one. */}
                    {term.not.length === 0
                      ? 'preferred, with nothing to replace'
                      : `instead of ${term.not.join(', ')}`}
                  </span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--icon btn--sm"
                      disabled={busy}
                      data-tip={`Remove "${term.use}"`}
                      onClick={() => setDropping(term.use)}
                    >
                      <Icon name="trash" size={13} label={`Remove ${term.use}`} />
                    </button>
                  )}
                </div>
                {term.because && <p className="home__quiet">{term.because}</p>}
                {dropping === term.use && (
                  <Confirm
                    says={`Pages using ${term.not.join(' or ') || 'the other spellings'} stop being warned about, and readers searching for them stop finding this page through an alias. Nothing on any page changes.`}
                    does="Remove the word"
                    cancel="Keep it"
                    busy={busy}
                    onYes={() => {
                      setDropping(null)
                      void saveGlossary(
                        { terms: glossary.terms.filter((t) => t.use !== term.use) },
                        `“${term.use}” is no longer in the glossary.`,
                      )
                    }}
                    onNo={() => setDropping(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <form
            className="vars__add"
            onSubmit={(e) => {
              e.preventDefault()
              const use = newUse.trim()
              if (use === '') return
              const not = newNot
                .split(',')
                .map((w) => w.trim().toLowerCase())
                .filter(Boolean)
              void saveGlossary(
                {
                  terms: [
                    ...glossary.terms.filter((t) => t.use !== use),
                    { use, not, ...(newBecause.trim() ? { because: newBecause.trim() } : {}) },
                  ],
                },
                `“${use}” is the word now.`,
              ).then(() => {
                setNewUse('')
                setNewNot('')
                setNewBecause('')
              })
            }}
          >
            <label className="library__field">
              <span>The word to use</span>
              <input
                className="library__alt"
                value={newUse}
                placeholder="workspace"
                disabled={busy}
                onChange={(e) => setNewUse(e.target.value)}
              />
            </label>
            <label className="library__field">
              <span>Instead of</span>
              <input
                className="library__alt"
                value={newNot}
                placeholder="organisation, org, account"
                disabled={busy}
                onChange={(e) => setNewNot(e.target.value)}
              />
            </label>
            <label className="library__field">
              <span>Why (optional)</span>
              <input
                className="library__alt"
                value={newBecause}
                placeholder="Billing is per workspace, and an account is a person."
                disabled={busy}
                onChange={(e) => setNewBecause(e.target.value)}
              />
            </label>
            <button className="btn btn--tinted" type="submit" disabled={busy || !newUse.trim()}>
              {busy ? <Icon name="spinner" size={13} /> : <Icon name="plus" size={13} />}
              Add the word
            </button>
          </form>
        )}
      </section>

      <section className="home__card home__card--wide">
        <h2>Style</h2>
        <p className="home__quiet">
          Short rules, not an essay: they go with every request for help with the words, where a
          rule a model can follow beats a paragraph it will summarise. {MOST_RULES} at most, and a
          rule is a line — {LONGEST_RULE} characters.
        </p>

        {(
          [
            ['voice', 'What this documentation does', 'Write in the present tense.'],
            ['avoid', 'What it does not', 'Marketing language.'],
          ] as const
        ).map(([which, label, example]) => (
          <div key={which} className="words__list">
            <div className="label">{label}</div>
            {style[which].length === 0 ? (
              <p className="nothing">Nothing said yet.</p>
            ) : (
              <ul className="redirects">
                {style[which].map((rule) => (
                  <li className="redirects__row" key={rule} data-rule={rule}>
                    <span className="redirects__from">{rule}</span>
                    {!readOnly && (
                      <button
                        className="btn btn--ghost btn--icon btn--sm"
                        disabled={busy}
                        data-tip="Remove this rule"
                        onClick={() =>
                          void saveStyle(
                            { ...style, [which]: style[which].filter((r) => r !== rule) },
                            'That rule is gone.',
                          )
                        }
                      >
                        <Icon name="trash" size={13} label="Remove" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!readOnly && rules < MOST_RULES && (
              <form
                className="settings__row"
                onSubmit={(e) => {
                  e.preventDefault()
                  const value = (which === 'voice' ? newVoice : newAvoid).trim()
                  if (value === '') return
                  void saveStyle(
                    { ...style, [which]: [...style[which], value.slice(0, LONGEST_RULE)] },
                    'Added.',
                  ).then(() => (which === 'voice' ? setNewVoice('') : setNewAvoid('')))
                }}
              >
                <label className="field">
                  <span className="label">Add a rule</span>
                  <input
                    className="input"
                    value={which === 'voice' ? newVoice : newAvoid}
                    placeholder={example}
                    maxLength={LONGEST_RULE}
                    disabled={busy}
                    onChange={(e) =>
                      which === 'voice' ? setNewVoice(e.target.value) : setNewAvoid(e.target.value)
                    }
                  />
                </label>
                <button className="btn" type="submit" disabled={busy}>
                  Add
                </button>
              </form>
            )}
          </div>
        ))}
        {rules >= MOST_RULES && (
          <p className="screen__warn">
            <Icon name="info" size={13} /> {MOST_RULES} rules is the cap — more than that is an
            essay, and it stops being followed. Remove one to add one.
          </p>
        )}
      </section>

      {/*
        The file itself, for the person who would rather write the YAML. It is
        the format, and hiding it would be the opposite of what this product
        promises about content.
      */}
      <p className="home__quiet">
        Both are files on the draft, like every page.{' '}
        <button className="linkish" onClick={() => onOpenPage(GLOSSARY)}>
          Open glossary.yaml
        </button>{' '}
        or{' '}
        <button className="linkish" onClick={() => onOpenPage(STYLE)}>
          open style.yaml
        </button>{' '}
        to write them by hand.
      </p>
    </div>
  )
}
